import { dateKeyRange } from '@/services/platform/data/events/types';

const LISBON = 'Europe/Lisbon';

/** Matches an ISO date-time with no timezone designator (naive, Europe/Lisbon wall-clock). */
const NAIVE_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/** UTC-anchored Date for a `YYYY-MM-DD` day key, so calendar-day formatting is stable. */
function dayKeyToDate(dateKey: string): Date {
  return new Date(`${dateKey}T00:00:00Z`);
}

/**
 * Resolves an event `startDateTime` to a Date + the timezone to format it in.
 * A naive value (no offset) is Europe/Lisbon wall-clock: anchor it to UTC and
 * format in UTC so the original hour/day numbers are preserved on any device.
 * A timezone-aware value is formatted in Europe/Lisbon.
 */
function resolveInstant(iso: string): { date: Date; timeZone: string } {
  if (NAIVE_DATETIME.test(iso)) return { date: new Date(`${iso}Z`), timeZone: 'UTC' };
  return { date: new Date(iso), timeZone: LISBON };
}

/**
 * True when a registration deadline exists and is now in the past. An absent or
 * unparseable value counts as open (never closed). Reuses `resolveInstant` so the
 * naive/UTC handling matches how event `startDateTime` is interpreted — the backend
 * sends the deadline as UTC, which `resolveInstant` resolves to the correct instant
 * whether or not the serialized value carries a `Z`.
 */
export function isRegistrationClosed(iso?: string | null): boolean {
  if (!iso) return false;
  const { date } = resolveInstant(iso);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() <= Date.now();
}

/** Group heading for a day, e.g. "APRIL 9". */
export function formatDayGroupLabel(dateKey: string, locale?: string): string {
  const formatted = new Intl.DateTimeFormat(locale || 'en-GB', {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(dayKeyToDate(dateKey));
  return formatted.toUpperCase();
}

/** Weekday + day of a `YYYY-MM-DD` key, with the month only when asked — "Thu 24 Sept" / "Thu 24". */
function formatSpanEnd(dateKey: string, locale: string | undefined, withMonth: boolean): string {
  return new Intl.DateTimeFormat(locale || 'en-GB', {
    weekday: 'short',
    day: 'numeric',
    ...(withMonth ? ({ month: 'short' } as const) : {}),
    timeZone: 'UTC',
  }).format(dayKeyToDate(dateKey));
}

/**
 * Event card date + time, e.g. "Thu, Apr 9 | 11:00pm" (rendered in Europe/Lisbon).
 *
 * With `endIso`, an event ending on a later day renders the span instead of just its start —
 * "Thu 24 – Fri 25 Sept | 6:30pm", or "Wed 30 Sept – Fri 2 Oct | 6:30pm" across months (the month
 * is written once when both ends share it). The time shown stays the start time.
 *
 * Which days the event covers comes from `dateKeyRange`, the same grace-window rule the day
 * bucketing and the calendar rail use, so a night running 19:30 → 01:00 still renders as one day
 * rather than claiming a two-day span the calendar would contradict.
 */
export function formatCardDateTime(
  iso?: string | null,
  locale?: string,
  endIso?: string | null
): string | undefined {
  if (!iso) return undefined;
  const { date, timeZone } = resolveInstant(iso);
  if (Number.isNaN(date.getTime())) return undefined;

  const timePart = new Intl.DateTimeFormat(locale || 'en-GB', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  })
    .format(date)
    .replace(/\s/g, '')
    .toLowerCase();

  const days = endIso ? dateKeyRange(iso, endIso) : [];
  if (days.length > 1) {
    const startKey = days[0];
    const endKey = days[days.length - 1];
    const sameMonth = startKey.slice(0, 7) === endKey.slice(0, 7);
    const spanPart = `${formatSpanEnd(startKey, locale, !sameMonth)} – ${formatSpanEnd(endKey, locale, true)}`;
    return `${spanPart} | ${timePart}`;
  }

  const datePart = new Intl.DateTimeFormat(locale || 'en-GB', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone,
  }).format(date);

  return `${datePart} | ${timePart}`;
}

export interface RailDay {
  dateKey: string;
  /** Month bucket (`YYYY-MM`) used to insert sticky month headers. */
  monthKey: string;
  day: string;
  weekday: string;
  monthShort: string;
}

/** Inclusive list of `YYYY-MM-DD` keys between two keys. */
export function eachDayKey(startKey: string, endKey: string): string[] {
  const [sy, sm, sd] = startKey.split('-').map(Number);
  const [ey, em, ed] = endKey.split('-').map(Number);
  const cursor = new Date(Date.UTC(sy, sm - 1, sd));
  const end = new Date(Date.UTC(ey, em - 1, ed));
  const keys: string[] = [];
  // Guard against an inverted range or bad input.
  let safety = 0;
  while (cursor <= end && safety < 800) {
    keys.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    safety += 1;
  }
  return keys;
}

/** First day of the month containing `dateKey` (`YYYY-MM-01`). */
export function startOfMonth(dateKey: string): string {
  return `${dateKey.slice(0, 7)}-01`;
}

/** Today's `YYYY-MM-DD` key in Europe/Lisbon wall-clock time. */
export function todayKey(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: LISBON }).format(new Date());
}

/** `dateKey` shifted forward by `days` calendar days. */
export function addDays(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Last day of the month containing `dateKey` (`YYYY-MM-DD`). */
export function endOfMonth(dateKey: string): string {
  const [y, m] = dateKey.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${dateKey.slice(0, 7)}-${String(lastDay).padStart(2, '0')}`;
}

/** Presentation fields for one calendar-rail day. */
export function toRailDay(dateKey: string, locale?: string): RailDay {
  const date = dayKeyToDate(dateKey);
  return {
    dateKey,
    monthKey: dateKey.slice(0, 7),
    day: new Intl.DateTimeFormat(locale || 'en-GB', { day: 'numeric', timeZone: 'UTC' }).format(date),
    weekday: new Intl.DateTimeFormat(locale || 'en-GB', { weekday: 'short', timeZone: 'UTC' }).format(date),
    monthShort: new Intl.DateTimeFormat(locale || 'en-GB', { month: 'short', timeZone: 'UTC' }).format(date),
  };
}
